import * as React from 'react'
import { Button } from 'semantic-ui-react'
import SemanticDatepicker from 'react-semantic-ui-datepickers'

import ConfirmModal from '../../components/modals/confirmModal'
import { Task } from '../../types'
import { toUtc0Timestamp } from '../../utils'

import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css'
import { useApi } from '../../store/mainContext'

interface Props {
  selectedTasks: Task[]
  isProcessing: boolean
  onProcessingChange(value: boolean): void
  setSelectedTaskIds: React.Dispatch<
    React.SetStateAction<Record<Task['_id'], true>>
  >
  mutateTasks(): Promise<unknown>
}

export default function BulkActionsBar(props: Props) {
  const {
    selectedTasks,
    isProcessing,
    onProcessingChange,
    setSelectedTaskIds,
    mutateTasks,
  } = props

  const api = useApi()
  const defaultDueDate = React.useMemo(() => {
    if (selectedTasks.length === 0) return null
    const [first] = [...selectedTasks].sort((a, b) => a.dueDate - b.dueDate)
    return first ? new Date(first.dueDate) : null
  }, [selectedTasks])

  const [selectedDate, setSelectedDate] = React.useState<Date | null>(
    defaultDueDate,
  )

  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false)

  React.useEffect(() => {
    setSelectedDate(defaultDueDate)
  }, [defaultDueDate])

  React.useEffect(() => {
    if (selectedTasks.length === 0) {
      setShowDeleteConfirm(false)
    }
  }, [selectedTasks.length])

  type OnDateChange = NonNullable<
    React.ComponentProps<typeof SemanticDatepicker>['onChange']
  >

  const handleDateChange: OnDateChange = async (_event, data) => {
    const { value } = data
    const nextValue = Array.isArray(value) ? value[0] : value
    const date = (nextValue as Date) ?? null
    setSelectedDate(date)

    if (!date || selectedTasks.length === 0 || isProcessing) return

    onProcessingChange(true)
    try {
      const dueDate = toUtc0Timestamp(date)
      await Promise.allSettled(
        selectedTasks.map((task) =>
          api.patch(`tasks/${task._id}`, { dueDate }),
        ),
      )
      await mutateTasks()
      setSelectedTaskIds({})
    } finally {
      onProcessingChange(false)
    }
  }

  const handleDelete = React.useCallback(() => {
    if (!selectedTasks.length || isProcessing) return
    setShowDeleteConfirm(true)
  }, [isProcessing, selectedTasks.length])

  const handleConfirmDelete = React.useCallback(async () => {
    if (!selectedTasks.length) {
      setShowDeleteConfirm(false)
      return
    }

    onProcessingChange(true)
    try {
      const tasksToDelete = [...selectedTasks]
      const results = await Promise.allSettled(
        tasksToDelete.map((task) => api.delete(`tasks/${task._id}`)),
      )

      const failedIds = results.reduce<string[]>((acc, result, index) => {
        const task = tasksToDelete[index]
        if (!task) return acc
        if (result.status === 'rejected') {
          console.error('Error deleting task', task._id, result.reason)
          acc.push(task._id)
        }
        return acc
      }, [])

      await mutateTasks()
      setSelectedTaskIds((prev) => {
        if (!failedIds.length) {
          return {}
        }
        const next: Record<Task['_id'], true> = {}
        failedIds.forEach((id) => {
          if (prev[id]) {
            next[id] = true
          }
        })
        return next
      })
    } finally {
      onProcessingChange(false)
      setShowDeleteConfirm(false)
    }
  }, [api, mutateTasks, onProcessingChange, selectedTasks, setSelectedTaskIds])

  const selectionLabel =
    selectedTasks.length === 1 ? 'task selected' : 'tasks selected'

  return (
    <div className="fixed bottom-6 left-1/2 z-[1100] -translate-x-1/2 px-4">
      <div className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white px-6 py-4 shadow-2xl md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-lg font-semibold">
            {selectedTasks.length} {selectionLabel}
          </div>
          <div className="text-base text-slate-500">
            Changes will apply to all selected tasks.
          </div>
        </div>

        <div className="ml-16 flex flex-1 flex-col gap-6 md:flex-row md:items-center md:justify-end">
          <div>
            <b className="mr-2">Set Due Date: </b>
            <SemanticDatepicker
              value={selectedDate}
              onChange={handleDateChange}
              clearOnSameDateClick={false}
              clearable
              datePickerOnly
              disabled={selectedTasks.length === 0 || isProcessing}
              showToday={true}
              pointing="top right"
              keepOpenOnSelect={true}
              keepOpenOnClear={false}
            />
          </div>

          <div className="flex items-center gap-3">
            <Button
              icon="trash alternate outline"
              color="red"
              onClick={handleDelete}
              disabled={isProcessing || selectedTasks.length === 0}
              loading={isProcessing}
            />
            <Button
              basic
              icon="delete"
              onClick={() => setSelectedTaskIds({})}
              disabled={isProcessing}
            />
          </div>
        </div>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        title="Delete selected tasks"
        question={
          selectedTasks.length === 1
            ? 'Delete the selected task?'
            : `Delete ${selectedTasks.length} selected tasks?`
        }
        highlight="This action cannot be undone."
        onCancel={() => {
          if (!isProcessing) {
            setShowDeleteConfirm(false)
          }
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
